import React, { createContext, useContext, useState, PropsWithChildren } from 'react';
import axios from 'axios';
import { snackbarTypes } from './snackbar';
import AuthContext from '../auth/AuthContext';
import { tokens } from '../../theme/tokens';
import type { IUser } from '../../types/Auth';

// Define the interface for context values
interface commonDetailsContextType {
    snackBarData: snackbarTypes;
    setSnackBarData: React.Dispatch<React.SetStateAction<snackbarTypes>>;
    isColorChange: boolean;
    setIsColorChange: React.Dispatch<React.SetStateAction<boolean>>;
    publishedBranding: any; // Global or single-parent branding
    setIsPublishedBranding: React.Dispatch<React.SetStateAction<any>>;
    clientScopedBranding: any; // Dynamic theme variable for ClientTracker, Tracknow defer, postaward defer
    setClientScopedBranding: React.Dispatch<React.SetStateAction<any>>;
    isBrandingLoaded: boolean;
    setIsBrandingLoaded: React.Dispatch<React.SetStateAction<boolean>>;
    isClientBrandingLoading: boolean;
    setIsClientBrandingLoading: React.Dispatch<React.SetStateAction<boolean>>;
    validateParentData: any;
    setValidateParentData: React.Dispatch<React.SetStateAction<any>>;
    fetchValidateParent: (targetUserId?: any) => Promise<any>;
    refreshUserRoleAndBranding: (targetUser?: any) => Promise<any>;
    fetchClientBranding: (targetCustomerId: any, customerType?: string) => Promise<any>;
    clearClientScopedBranding: () => void;
    brandingCacheRef: React.MutableRefObject<Map<string, any>>;
}

// Create context with initial values
const CommonDetailsContext = createContext<commonDetailsContextType | undefined>(undefined);

// Create a provider component to wrap your app
export const CommonDetailsProvider = ({ children }: PropsWithChildren<{}>) => {
    const [snackBarData, setSnackBarData] = useState<snackbarTypes>({
        snackBarIopen: false,
        snackBarMessage: '',
    });
    const [isColorChange, setIsColorChange] = useState(false);
    const [publishedBranding, setIsPublishedBranding] = useState<any>(null);
    const [clientScopedBranding, setClientScopedBranding] = useState<any>(null);
    const [isBrandingLoaded, setIsBrandingLoaded] = useState(false);
    const [isClientBrandingLoading, setIsClientBrandingLoading] = useState(false);
    const [validateParentData, setValidateParentData] = useState<any>(null);
    const brandingCacheRef = React.useRef<Map<string, any>>(new Map());

    const authContext = useContext(AuthContext);
    const { authUser: currentAuthUser, portalUser, setAuthStatus, setAuthorized, setAuthUser, setPermission } = authContext || {};

    const fetchBrandingDetails = async (custId: any, customerType?: string) => {
        const API_BASE_URL = process.env.REACT_APP_TRKNW_API_BASE_URL;
        try {
            const brandingRes = await axios.get(
                `${API_BASE_URL}/Application/GetBrandingConfiguration?CustomerId=${custId}&CustomerType=${customerType}`
            );
            return brandingRes;
        } catch (err) {
            console.log("fetchBrandingDetails error", err);
            return null;
        }
    };

    const fetchValidateParent = async (targetUserId?: any) => {
        const userId = targetUserId ?? currentAuthUser?.userId ?? portalUser?.userId;
        if (userId === undefined || userId === null) return null;
        const API_BASE_URL = process.env.REACT_APP_TRKNW_API_BASE_URL;
        try {
            const validateRes = await axios.get(
                `${API_BASE_URL}/Customer/validate-parent/${userId}`
            );
            const data = validateRes.data?.data;
            setValidateParentData(data);
            return data;
        } catch (err) {
            console.log("fetchValidateParent error:", err);
            return null;
        }
    };

    const refreshUserRoleAndBranding = async (targetUser?: any) => {
        const user = targetUser || portalUser;
        const userEmail = user?.email || user?.loginId;
        const API_BASE_URL = process.env.REACT_APP_TRKNW_API_BASE_URL;

        try {
            const res = await axios.get(`${API_BASE_URL}/Application/UserRole`);
            const allRoles = Array.isArray(res.data) ? res.data : [];

            if (userEmail) {
                const findRole = (ele: any) =>
                    ele?.loginId?.toLowerCase() === userEmail.toLowerCase();
                const userRoles = allRoles.filter(findRole);
                const roleType = userRoles.map((ele: any) => ele.roleName);
                const roleDetails = userRoles;
                const authUser = userRoles[0] as IUser | undefined;

                if (roleType && roleType.length > 0 && authUser) {
                    if (setAuthStatus && setAuthorized && setAuthUser && setPermission) {
                        setAuthStatus(true, userEmail, roleType as any, authUser.userId, roleDetails);
                        setPermission(userRoles);
                        authUser.clients = userRoles
                            .filter((ele: any) => ele.customerId !== null && ele.customerId !== undefined)
                            .map((ele: any) => ele.customerId);

                        try {
                            const validateRes = await axios.get(
                                `${API_BASE_URL}/Customer/validate-parent/${authUser.userId}`
                            );
                            const brandingHierarchyResponse = validateRes.data?.data;
                            setValidateParentData(brandingHierarchyResponse);
                            let brandingRes = null;
                            if (
                                brandingHierarchyResponse?.totalAssignedCustomers > 0 &&
                                brandingHierarchyResponse?.isSingleParent
                            ) {
                                brandingRes = await fetchBrandingDetails(brandingHierarchyResponse.parentCustomerId, "Parent");
                            }
                            const data =
                                brandingRes?.data?.data ||
                                (Array.isArray(brandingRes?.data) ? brandingRes?.data[0] : brandingRes?.data);
                            if (data && (data.primaryColor || data.PrimaryColor)) {
                                setIsPublishedBranding({
                                    brandingId: data.brandingId ?? data.BrandingId ?? data.id ?? data.Id,
                                    customerId: data.customerId ?? data.CustomerId ?? authUser.customerId,
                                    primaryColor: data.primaryColor || data.PrimaryColor,
                                    backgroundColor: data.backgroundColor || data.BackgroundColor || tokens.colors.white,
                                    fontFamily: data.fontFamily || data.FontFamily || "Roboto",
                                    logoDataUrl: data.logoDataUrl || data.LogoDataUrl || "",
                                    userId: data.userId ?? data.UserId ?? "",
                                    userName: data.userName || data.UserName || "",
                                    createUser: data.createUser || data.CreateUser || "",
                                    createDate: data.createDate || data.CreateDate || data.publishedAt || data.PublishedAt || "",
                                    updateUser: data.updateUser || data.UpdateUser || "",
                                    updateDate: data.updateDate || data.UpdateDate || "",
                                    version: data.version ?? data.Version,
                                });
                            } else {
                                setIsPublishedBranding(null);
                            }
                        } catch (brandingErr) {
                            console.log("GetBrandingConfiguration error:", brandingErr);
                            setValidateParentData(null);
                            setIsPublishedBranding(null);
                        } finally {
                            setAuthUser(authUser);
                            setAuthorized(true);
                            setIsBrandingLoaded(true);
                        }
                    }
                } else {
                    setAuthUser && setAuthUser(null);
                    setValidateParentData(null);
                    setIsPublishedBranding(null);
                    setIsBrandingLoaded(true);
                }
            } else {
                setIsBrandingLoaded(true);
            }
        } catch (error) {
            console.log("refreshUserRoleAndBranding error:", error);
            setIsBrandingLoaded(true);
        }
    };

    const fetchClientBranding = async (targetCustomerId: any, customerType?: string) => {
        if (!targetCustomerId) {
            setClientScopedBranding(null);
            setIsClientBrandingLoading(false);
            return null;
        }

        const cacheKey = customerType ? `${targetCustomerId}_${customerType}` : String(targetCustomerId);
        if (brandingCacheRef.current.has(cacheKey)) {
            const cached = brandingCacheRef.current.get(cacheKey);
            setClientScopedBranding(cached);
            setIsClientBrandingLoading(false);
            console.log("fetchClientBranding: Using cached branding for customerId:", "targetCustomerId", targetCustomerId, "customerType", customerType, "cacheKey", cacheKey, "brandingCacheRef", brandingCacheRef.current);
            return cached;
        }

        setIsClientBrandingLoading(true);
        try {
            const brandingRes = await fetchBrandingDetails(targetCustomerId, customerType);
            const data =
                brandingRes?.data?.data ||
                (Array.isArray(brandingRes?.data) ? brandingRes?.data[0] : brandingRes?.data);
            if (data && (data.primaryColor || data.PrimaryColor)) {
                const normalized = {
                    brandingId: data.brandingId ?? data.BrandingId ?? data.id ?? data.Id,
                    customerId: data.customerId ?? data.CustomerId ?? targetCustomerId,
                    primaryColor: data.primaryColor || data.PrimaryColor,
                    backgroundColor: data.backgroundColor || data.BackgroundColor || tokens.colors.white,
                    fontFamily: data.fontFamily || data.FontFamily || "Roboto",
                    logoDataUrl: data.logoDataUrl || data.LogoDataUrl || "",
                    userId: data.userId ?? data.UserId ?? "",
                    userName: data.userName || data.UserName || "",
                    createUser: data.createUser || data.CreateUser || "",
                    createDate: data.createDate || data.CreateDate || data.publishedAt || data.PublishedAt || "",
                    updateUser: data.updateUser || data.UpdateUser || "",
                    updateDate: data.updateDate || data.UpdateDate || "",
                    version: data.version ?? data.Version,
                };
                brandingCacheRef.current.set(cacheKey, normalized);
                setClientScopedBranding(normalized);
                return normalized;
            } else {
                brandingCacheRef.current.set(cacheKey, null);
                setClientScopedBranding(null);
                return null;
            }
        } catch (err) {
            console.log("fetchClientBranding error:", err);
            setClientScopedBranding(null);
            return null;
        } finally {
            setIsClientBrandingLoading(false);
        }
    };

    const clearClientScopedBranding = () => {
        setClientScopedBranding(null);
        setIsClientBrandingLoading(false);
    };

    return (
        <CommonDetailsContext.Provider value={{
            snackBarData, setSnackBarData,
            isColorChange, setIsColorChange,
            publishedBranding, setIsPublishedBranding,
            clientScopedBranding, setClientScopedBranding,
            isBrandingLoaded, setIsBrandingLoaded,
            isClientBrandingLoading, setIsClientBrandingLoading,
            validateParentData, setValidateParentData,
            fetchValidateParent,
            refreshUserRoleAndBranding,
            fetchClientBranding,
            clearClientScopedBranding,
            brandingCacheRef,
        }}>
            {children}
        </CommonDetailsContext.Provider>
    );
};

// Create a custom hook for using TitlesContext
export const useCommonDetailsContext = () => {
    const context = useContext(CommonDetailsContext);
    if (!context) {
        throw new Error('useCommonDetailsContext must be used within a CommonDetailsProvider');
    }
    return context;
};

export default CommonDetailsContext;
